/* import __COLOCATED_TEMPLATE__ from './video-player.hbs'; */
import { not } from '@ember/object/computed';
import Component from '@ember/component';
import { computed, set } from '@ember/object';
import moment from 'moment';
import { requestFullscreen } from '@square/glass-ui/utils/video';
import { bind } from '@ember/runloop';
import { inject as service } from '@ember/service';
import PauseIcon from '@square/glass-ui/components/svg/icon-pause';
import PlayIcon from '@square/glass-ui/components/svg/icon-play';
/**

The video player component can embed a responsive video component in the page with
a `<video>` tag

**NOTE** If you're loading the video from your localhost, there will be a bug in Chrome where the
playback slider will reset to the beginning of the video when attempting to skip to a specific point
by clicking or dragging the playback slider. This behavior does not occur if you load the video
properly from a source like AWS or a CDN.

```example

@title Basic Usage
@description The `video` element fills in the width of its container and the height
auto-adjusts. You can pass a `class` and give the video a static width with CSS if needed.
By default, the video type is `video/mp4`, but you can pass a different type for better
optimization. Don't forget to add accessible `alt` text!

@hbs
{{video-player src="/assets/videos/video-example.mp4"
               type="video/mp4"
               alt="My Example Video"
               poster="/assets/images/video-example-poster.png"}}

```
Renders:

```
<div class="video-player">
  <video class="video-player" src="assets/videos/video-example.mp4" type="video/mp4">
    <p>Your browser does not support HTML5 video. assets/videos/video-example.mp4</p>
  </video>
  <div class="video-player-controls">
    ...
  </div>
</div>
```

```example

@title Custom Overlay
@description You can use the component as a block to pass custom text or template to display
as the video overlay with the play icon. Note that at <= small viewports, anything that is not
an <h2> will be hidden.

@hbs
{{#video-player src="/assets/videos/video-example.mp4"
               type="video/mp4"
               alt="My Example Video"
               poster="/assets/images/video-example-poster.png"
               as |video|}}
  <h2>Custom Overlay Text</h2>
  <p>Maybe a description of the video and a duration: {{video.duration}}</p>

{{/video-player}}

```

```example
@title Custom Theming
@description You can use the CSS mixin `VideoPlayerTheme` and pass a custom color to
theme the video player controls with a color of your choice. (If your video is in a container
that uses the `TieDye` mixin, you don't need to do this! The TieDie mixin will theme any videos
inside of it.)

@hbs
{{video-player src="/assets/videos/video-example.mp4"
               poster="/assets/images/video-example-poster.png"
               class="my-custom-themed-video-player"}}
```

```
// You can pass a color variable or a regular color value (hex/rgba) here
.my-custom-themed-video-player {
  @include VideoPlayerTheme(#924cc4)
}

```

```example

@title Autoplay
@description Pass `autoplay=true` to enable autoplay. Due to Chrome restrictions, video will be muted. For mobile
devices, video will play inline.

@hbs
{{video-player src="/assets/videos/video-example.mp4"
               type="video/mp4"
               alt="My Example Video"
               poster="/assets/images/video-example-poster.png"
               autoplay=true}}
```

```example
@title Background Videos (Hiding player UI)
@description You can hide the controls bar and the overlay icon if you need something akin to
a background video (where you don't want the user to interact with it) by passing `canInteract=false`
which will hide the overlay and the controls. You probably also want to pass `autoplay=true`,
`loop=true` and `playsinline=true`

@hbs
{{video-player src="/assets/videos/video-example.mp4"
               poster="/assets/images/video-example-poster.png"
               class="my-custom-themed-video-player"
               canInteract=false
               autoplay=true
               loop=true
               playinline=true}}
```

### Parameters
@param {String} [src] The source of the video. Both external link or video placed in public/ works.
@param {String} [type="video/mp4"] Declare the type of the source for the optimization (optional)
@param {Boolean} [autoplay=false] Autoplay video (optional)
@param {Boolean} [loop=false] Loop video (optional)
@param {Boolean} [playsinline=false] Play video inline in mobile browsers (optional)
@param {Boolean} [canInteract=true] Set this to false if you need something akin to a background video where you don't want the user to be able to interact with it
@param {String} [description] Video alt text
@param {String} [poster] Poster image src. Shows while the video is loading
@param {Action} [onPlay] Send this action when the video plays
@param {Action} [onPause] Send this action when the video pauses
@param {Action} [onEnd] Send this action when the video ends
@param {Action} [onTimeUpdate] Send this action on video time update.
*/

function convertDurationToMinuteSeconds(duration) {
  const momentDuration = moment.duration(duration, 'seconds');
  const minutes = momentDuration.minutes();
  let seconds = momentDuration.seconds();
  seconds = `0${seconds}`.slice(-2); // prepend 0 to seconds if it's single digit
  return `${minutes}:${seconds}`;
}

// eslint-disable-next-line ember/no-classic-classes
export default Component.extend({
  classNames: ['video-player'],
  classNameBindings: ['isPlaying:video-player--is-playing', 'autoplayAtFirst:video-player--initial-autoplay', 'showControlsOnHover:video-player--show-controls-on-hover', 'hasPlayed:video-player--active'],
  attributeBindings: ['style'],
  assetMap: service(),
  isPlaying: false,
  isMuted: false,
  type: 'video/mp4',
  autoplay: false,
  loop: false,
  playsinline: false,
  videoProgress: 0,
  videoVolume: 1,
  canInteract: true,
  isTouchStartSupported: false,
  togglePlayOnTouchEnd: false,
  isAtBeginning: true,
  hasPlayed: false,
  hasEnded: false,
  showControlsOnHover: not('isAtBeginning'),
  // We call `video.load()` because we want to show the poster
  // when the video finishes playing. If the `autoplay` attribute is
  // set when `video.load()` is called, the video would start playing
  // rather than showing the poster. This allows us to remove the
  // autoplay attribute from the video tag once it has played.
  autoplayAtFirst: computed('autoplay', 'hasPlayed', function () {
    return this.get('autoplay') && !this.get('hasPlayed');
  }),
  // Prevent the poster from flashing during loading the video when autoplaying
  autoplayAwarePoster: computed('autoplay', 'hasEnded', 'poster', function () {
    if (this.get('autoplay') && !this.get('hasEnded')) {
      return null;
    }
    return this.get('poster');
  }),
  style: computed('_styleOverride', 'poster', {
    get() {
      if (this._styleOverride !== undefined) {
        return this._styleOverride;
      }
      const posterUrl = this.assetMap.resolve(this.poster);
      return `background-image: url(${posterUrl})`;
    },
    set(_, value) {
      return set(this, '_styleOverride', value);
    }
  }),
  overlayIcon: computed('isPlaying', function () {
    return this.isPlaying ? PauseIcon : PlayIcon;
  }),
  currentVideoTime: computed('videoProgress', 'duration', function () {
    return this.get('videoProgress') * this.duration;
  }),
  currentDisplayTime: computed('currentVideoTime', function () {
    return convertDurationToMinuteSeconds(this.get('currentVideoTime'));
  }),
  video: computed('element', function () {
    return this.element.getElementsByClassName('video-player__video')[0];
  }),
  lastVolume: 0,
  didInsertElement(...args) {
    this._super(...args);
    this.setVideoEventHandlers();
    this.get('video').addEventListener('loadedmetadata', bind(this, this.onloadedmetadata));
    this.get('video').addEventListener('timeupdate', bind(this, this.ontimeupdate));
    this.get('video').addEventListener('ended', bind(this, this.onend));
    this.get('video').addEventListener('play', bind(this, this.onplay));
    this.get('video').addEventListener('pause', bind(this, this.onpause));
    if (this.get('autoplay')) {
      // if user passes in playsinline, then we mute the video (mobile)
      if (this.get('playsinline')) {
        // chrome requires autoplay video to be muted
        this.mute();
      }
    }
  },
  willDestroyElement(...args) {
    this._super(...args);

    // pause video before teardown to avoid timeupdate race conditions
    this.pause();
    this.get('video').removeEventListener('loadedmetadata', this.onloadedmetadata);
    this.get('video').removeEventListener('timeupdate', this.ontimeupdate);
    this.get('video').removeEventListener('ended ', this.onend);
    this.get('video').removeEventListener('play', this.onplay);
    this.get('video').removeEventListener('pause', this.onpause);
  },
  setVideoEventHandlers() {
    // Set the video duration after video metadata has loaded
    this.onloadedmetadata = () => {
      if (this.isDestroyed) {
        return;
      }

      // this prevents a weird flash of the placeholder image
      // after the video is loaded.
      this.set('style', null);
      const duration = this.get('video').duration;
      this.set('duration', duration);
      const displayedDuration = convertDurationToMinuteSeconds(duration);
      this.set('displayedDuration', displayedDuration);
    };

    // Update the slider bar as the video plays
    this.ontimeupdate = () => {
      if (!this.isPlaying || this.isDestroyed) {
        return;
      }
      const previousProgress = this.get('videoProgress');
      const currentProgress = this.get('video').currentTime / this.get('video').duration;
      if (this.onTimeUpdate) {
        this.onTimeUpdate(previousProgress, currentProgress);
      }
      this.set('videoProgress', currentProgress);
    };

    // Set video to isPlaying false to update the pause/play icon
    this.onend = () => {
      if (this.isDestroyed) {
        return;
      }
      this.set('isPlaying', false);
      this.set('hasEnded', true);
      this.resetVideoToBeginning(); // shows poster again
      if (this.onEnd) {
        this.onEnd();
      }
    };
    this.onplay = () => {
      if (this.isDestroyed) {
        return;
      }
      this.set('hasPlayed', true);
      this.set('isPlaying', true);
      this.set('isAtBeginning', false);
      if (this.onPlay) {
        this.onPlay();
      }
    };
    this.onpause = () => {
      if (this.isDestroyed) {
        return;
      }
      this.set('isPlaying', false);
      if (this.onPause) {
        this.onPause();
      }
    };
  },
  resetVideoToBeginning() {
    this.get('video').load();
    this.set('videoProgress', 0);
    this.set('isAtBeginning', true);
  },
  // Updates currentTime of video
  updatetime(e, progress) {
    this.set('videoProgress', progress);
    this.get('video').currentTime = this.get('currentVideoTime');
  },
  // May return promise; older browsers may return nothing
  play() {
    return this.get('video').play();
  },
  pause() {
    this.get('video').pause();
  },
  // Setup play flag and events for cancelling play on touchmove & touchcancel
  allowPlayMobile() {
    this.set('isTouchStartSupported', true);
    this.set('togglePlayOnTouchEnd', true);
    this.cancelPlayMobileListener = this.cancelPlayMobile.bind(this);
    this.get('video').addEventListener('touchmove', bind(this, this.cancelPlayMobileListener), {
      once: true
    });
    this.get('video').addEventListener('touchcancel', bind(this, this.cancelPlayMobileListener), {
      once: true
    });
  },
  cancelPlayMobile() {
    if (this.isDestroyed) {
      return;
    }
    this.set('togglePlayOnTouchEnd', false);
  },
  togglePlayMobile() {
    if (this.get('togglePlayOnTouchEnd')) {
      const playAction = this.togglePlay();
      if (playAction) {
        playAction.catch(function () {}); // iOS may return permission error on touch and hold; we can't recover from it
      }
    }
    this.get('video').removeEventListener('touchmove', this.cancelPlayMobileListener);
    this.get('video').removeEventListener('touchcancel', this.cancelPlayMobileListener);
  },
  togglePlayDesktop() {
    if (!this.get('isTouchStartSupported')) {
      this.togglePlay();
    }
  },
  togglePlay() {
    // Only toggle play/pause on click if this video is set
    // to be interactive (e.g. not a background/layout video)
    if (this.canInteract) {
      return this.isPlaying ? this.pause() : this.play();
    }
    return null;
  },
  mute() {
    this.set('lastVolume', this.get('video').volume);
    this.set('videoVolume', 0);
    this.get('video').muted = true;
    this.set('isMuted', true);
  },
  unmute() {
    this.set('videoVolume', this.lastVolume);
    this.get('video').muted = false;
    this.set('isMuted', false);
  },
  fullScreen() {
    const video = this.get('video');
    requestFullscreen(video);
  },
  // volume is a value from 0 to 1
  changeVolume(e, value) {
    // ensures the steps for volume are to the tenths
    const videoVolume = Number(value.toFixed(1));
    this.get('video').volume = videoVolume;
    this.set('videoVolume', videoVolume);
    this.set('isMuted', videoVolume === 0);
  }
});